import type { AdjustmentWhyValue } from '@shared-ui/components';
import type { ActivityEngagementSeries, AdminStats, ApiResponse, AsyncJobConfigValues, AsyncJobTypeConfig, AsyncJobTypeStats, ConceptLabEntry, HintRequest, LearningMomentRun, PaginatedResponse, QuestionSourcesResponse, Submission } from '../types';
import { getErrorMessage } from '../types';
import type { AppSettings, ProviderConfig, ProviderConfigDoc, PipelineConfigDoc, OperationsMap, ProviderTieredSet, ProviderParallelSet, ConfigBundle, ConfigSectionName, ConfigImportDryRunResult, ConfigImportApplyResult, ConfigImportRequest } from '../types/settings';
import type { PipelineConfigRevision, PipelineConfigRevisionSummary } from '../types/pipelineConfig';
import { authApi } from './auth';
import { API_BASE, fetchWithAdmin } from './fetchHelper';
import { waitForAsyncJob, type PolledJobView } from '@shared-ui/api';
import type { PaginatedSolutionSplits, SolutionSplitDetail } from '../pages/operations/solutionSplitterTypes';

// Re-export split modules
export { questionApi, importApi } from './questionApi';
export { modelApi } from './modelApi';
export { userApi } from './userApi';

// Re-export all types from split modules
export type {
  RateLimitUserSummary,
  RateLimitStatsResponse,
  RateLimitEvent,
  RateLimitUserDetail,
  StudentTeacherControlBreakdown,
  StudentTeacherControlsResponse,
  FeatureGateDefinition,
  UserFeatureGateView,
  ExperimentDefinition,
  ExperimentVariant,
  ProviderCostSummary,
  CostPeriod,
  UserCostsResponse,
  ImpersonateResponse,
  NotificationRolloutEntry,
  NotificationRow,
  NotificationDriftItem,
  NotificationRolloutResponse,
} from './userApi';

// Pipeline observability — see Backend/functions/src/admin/analytics/getPipelineMetrics.ts.
// Keep field names in sync with the backend response contract.
export interface PipelineSlice {
  promptId: string;
  promptName?: string;
  promptType?: string;
  promptVersion: number;
  model: string;
  provider: string;
  runs: number;
  primaryRuns: number;
  costSum: number;
  latencyAvgMs: number;
  escalatedAwayRuns: number;
  escalationRate: number;
}
export interface PipelineQualityCheckStat {
  name: string;
  present: number;
  fired: number;
  fireRate: number;
}
/** Pipeline Metrics → one quality check, grouped by the question it fired on. */
export interface QualityCheckRun {
  outputId: string;
  submissionId?: string;
  generatedAt: string;
  /** Absent on eval-harness runs, which have no user behind them. */
  userId?: string;
  userName?: string;
  model?: string;
  promptId?: string;
  promptName?: string;
  promptVersion?: number;
  isPrimary?: boolean;
  /** Wall-clock of the model call. */
  latencyMs?: number;
  signals?: Record<string, unknown>;
}
/** The standing ruling on a question's answer key. Present only on the
 *  objective_llm_answer_mismatch drill-down — the only check that raises one. */
export interface SuspectAnswerKeyFlag {
  key: string;
  modelAnswers: string[];
  /** All-time, unlike the group's window-scoped `fired`. */
  fireCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  status: 'open' | 'accepted' | 'dismissed';
  resolvedBy?: string;
  resolvedAt?: string;
}
export interface QualityCheckQuestionGroup {
  questionId: string;
  questionText: string;
  subject?: string;
  fired: number;
  runs: QualityCheckRun[];
  suspectAnswerKey?: SuspectAnswerKeyFlag;
}
export interface QualityCheckDrilldown {
  name: string;
  window: { from: string; to: string; source: string };
  fired: number;
  /** Firings whose ModelInput is gone, so no question could be attributed. */
  unattributed: number;
  questions: QualityCheckQuestionGroup[];
}

export interface PipelineMetrics {
  window: { from: string; to: string; source: string };
  slices: PipelineSlice[];
  escalationTriggers: { trigger: string; count: number }[];
  qualityChecks: PipelineQualityCheckStat[];
  totals: { runs: number; primaryRuns: number; costSum: number; escalatedAwayRuns: number };
}

// Lifecycle funnel — see Backend/functions/src/lifecycle/getLifecycleFunnelEndpoint.ts.
export interface LifecycleFunnelStep {
  event: string;
  users: number;
  pctOfStart: number;
  pctOfPrev: number;
}
// Pick-screen branch off first_run_started: proceeded to a question vs. skipped.
export interface FirstRunSplit {
  started: number;
  proceeded: number;
  proceededPct: number;
  skipped: number;
  skippedPct: number;
}
// Habit milestone: solved a question after completing first-run, by surface.
export interface HabitSolve {
  solved: number;
  pctOfStart: number;
  bySource: { source: string; users: number }[];
}
export interface LifecycleFunnel {
  cohortWeek: string;
  availableWeeks: string[];
  experimentKey?: string;
  steps: LifecycleFunnelStep[];
  firstRunSplit: FirstRunSplit;
  habitSolve: HabitSolve;
  byVariant?: { variant: string; steps: LifecycleFunnelStep[] }[];
  /** Set when sliced by where people came from — read from telemetry tier 2. */
  sliceBy?: string;
  bySlice?: { slice: string; steps: LifecycleFunnelStep[] }[];
  /** What `sliceBy` accepts, from the server. Never hardcode it here: a copy
   *  kept offering `channel` after the rename to `placement`, and the server
   *  ignored it without a word. */
  sliceOptions: string[];
  /** Set when steps were counted only within N days of each person's arrival. */
  windowDays?: number;
  /** Set when `segmentId` narrowed who the funnel is about. Every table on the
   *  page — steps, habit, arms, slices — counts this population. */
  /** Set when `link` narrowed the funnel to a published link's people. */
  link?: string;
  segment?: {
    id: string;
    name: string;
    criteria: Record<string, string | number>;
    evaluatedAt: string;
    /** People at the first step — what every percentage is a fraction of. */
    population: number;
    /** False when the criteria name behaviour: a different set of people each day. */
    stable: boolean;
    /** More people matched than one query carries; the population is a floor. */
    truncated?: boolean;
  };
}

// Dark/light usage — see Backend/functions/src/admin/analytics/getThemeStats.ts.
export interface ThemeStats {
  days: number;
  since: string;
  byDay: { date: string; light: number; dark: number; unknown: number }[];
  totals: { light: number; dark: number; unknown: number; darkPct: number };
}

// Active users + cohort retention — see Backend/functions/src/admin/analytics/getRetentionStats.ts.
export interface RetentionStats {
  days: number;
  since: string;
  /** WAU/MAU are rolling: distinct users active in the trailing 7 / 28 days ending on `date`. */
  daily: { date: string; dau: number; wau: number; mau: number }[];
  /** The last 12 signup weeks, oldest first — fixed, not tied to `days`. */
  cohorts: { cohortWeek: string; size: number; weeklyActive: number[] }[];
  /** The last 12 signup months, oldest first. Absent from a backend older than it. */
  monthlyCohorts?: { cohortMonth: string; size: number; monthlyActive: number[] }[];
  /** The slice the cohort grids were cut to; the daily charts are always everyone. */
  segment?: RetentionSegment;
  totals: { dau: number; wau: number; mau: number; stickiness: number };
}

/** Where a signup came from — see Backend/functions/src/admin/analytics/retentionSegment.ts. */
export type SignupSource = 'self' | 'teacher_invite' | 'school' | 'admin';
/** Linked to a teacher now (an active enrollment), or not. */
export type TeacherLink = 'with' | 'without';
export interface RetentionSegment { source?: SignupSource; teacher?: TeacherLink }

function setSegment(p: URLSearchParams, segment?: RetentionSegment): void {
  if (segment?.source) p.set('source', segment.source);
  if (segment?.teacher) p.set('teacher', segment.teacher);
}

// One cohort-grid cell, drilled down — see admin/analytics/getCohortUsers.ts.
// The backend refuses cells above this, so the grid only offers the drill-down
// below it. Keep in sync with COHORT_DRILLDOWN_MAX there.
export const COHORT_DRILLDOWN_MAX = 20;
export interface CohortUser { id: string; name?: string; email?: string; signedUpOn: string }
export interface CohortUsers {
  /** `cohortWeek` / `weekIndex` / `weekStart` are in this unit — a month's
   *  first day and months, for a monthly cell. */
  grain?: 'week' | 'month';
  cohortWeek: string;
  weekIndex: number;
  weekStart: string;
  cohortSize: number;
  users: CohortUser[];
}

// Cost analytics response — see Backend/functions/src/admin/analytics/getCostAnalytics.ts.
export interface CostSlice { key: string; usd: number; calls: number }
export interface CostDailyPoint {
  date: string;
  total: number;
  byOperation: Record<string, number>;
  byBucket: Record<string, number>;
}
export interface StudentCostRow {
  userId: string; name: string; email: string;
  usd: number; calls: number; topOperation: string; teacherCount: number;
}
export interface TeacherCostRow {
  teacherId: string; name: string;
  ownUsd: number; studentsUsd: number; totalUsd: number; studentCount: number;
}
export interface CostAnalyticsResponse {
  period: { days: number; from: string; to: string; maxDays: number };
  summary: {
    totalUsd: number; attributedUsd: number; unattributedUsd: number;
    calls: number; activeUsers: number; costPerActiveUser: number;
  };
  daily: CostDailyPoint[];
  byOperation: CostSlice[];
  byProvider: CostSlice[];
  byBucket: CostSlice[];
  students: StudentCostRow[];
  teachers: TeacherCostRow[];
}

// Teacher stats response — see Backend/functions/src/admin/analytics/getTeacherStats.ts.
// Keep field names in sync with the backend.
export interface TeacherStatsResponse {
  teacherId: string;
  rangeDays: 7 | 28 | 56;
  /** `enrollment` for per-teacher; `signup` for the all-teachers / tenant aggregate. */
  cohortAnchor: 'enrollment' | 'signup';
  totalStudents: number;
  visited7d: number;
  engaged7d: number;
  practised7d: number;
  questionsAttempted7d: number;
  cumulativeStudents: { date: string; total: number }[];
  activeStudentsTrend: { date: string; value: number }[];
  questionsAttemptedTrend: { date: string; value: number }[];
  grain: 'daily' | 'weekly';
  activityEngagement: ActivityEngagementSeries[];
  weeklyRetention: {
    weekStart: string;
    activeAny: number;
    activeAttempted: number;
    retainedAny: number;
    retainedAttempted: number;
  }[];
  /** Last 12 weeks / last 12 months of cohorts, oldest first. Index n of
   *  `activeAny` / `activeAttempted` is n periods after the anchor period. */
  cohortWeekly: TeacherCohortRow[];
  cohortMonthly: TeacherCohortRow[];
}
export interface TeacherCohortRow {
  start: string;
  size: number;
  activeAny: number[];
  activeAttempted: number[];
}

/** One question-extraction run — the durable record behind Create Assignment
 *  from PDF/Word. See docs/FeatureDesigns/operation-run-records.md. */
export interface QuestionExtractionRunView {
  id: string;
  operation: 'extractVerbatim' | 'extractVerbatimText';
  status: 'running' | 'succeeded' | 'failed';
  createdBy: string;
  startedAt: string;
  completedAt?: string;
  error?: string;
  sourceKind: 'pdf' | 'docx';
  sourceFileName?: string;
  sourceFileUrl?: string;
  /** Owning tenant — `tenantId` is on every run; name and slug are resolved by
   *  the detail endpoint. */
  tenantId?: string;
  tenantName?: string;
  tenantSlug?: string;
  correlation?: { questionSetId?: string };
  asyncJobId?: string;
  createdQuestionIds?: string[];
  extracted?: number;
  withDiagram?: number;
  enriched?: number;
  enrichmentFailed?: number;
  // Validation judge — present iff the pipeline carries a `validate_extraction`
  // step. See docs/FeatureDesigns/extraction-validation-judge.md.
  validated?: number;
  validationSuspect?: number;
  validationWrong?: number;
  validationMissed?: number;
  validationCostUsd?: number;
  // Which model served the judge, and whether it differed from the extraction's.
  // Absent means no judge ran — not the same as not independent.
  validationJudgeProvider?: string;
  validationJudgeIndependent?: boolean;
  validationIssues?: Array<{
    slot?: number;
    questionNumber?: string;
    questionText?: string;
    verdict: 'suspect' | 'wrong';
    confidence?: number;
    field: string;
    severity: string;
    detail: string;
    suggestion?: string;
  }>;
  // DOCX equation counters. The converter these count is what stands between a
  // formula-heavy Word document and a run that "succeeds" with invented numbers.
  equations?: number;
  equationsConverted?: number;
  // Options wrong on their face (all identical / duplicated). Free and
  // deterministic — no second model, no ground truth. Zero is written
  // explicitly, so absent means the run predates the check, not that it passed.
  optionDefectCount?: number;
  optionDefects?: Array<{ slot: number; questionNumber: string; kind: string; detail: string }>;
  // Pages the model was given, and how many the page cap left out — `dropped > 0`
  // means questions are missing. Absent on the DOCX path, which has no pages.
  pages?: { read: number; dropped: number };
  // Cross-model agreement — present iff the extractionCrossCheck gate is on for
  // that teacher. Where two models read the same page differently, at least one
  // is wrong; where they fail the same way, this sees nothing.
  crossCheckProvider?: string;
  crossCheckAgreed?: number;
  crossCheckDisagreed?: number;
  crossCheckOnlyInOne?: number;
  crossCheckReview?: Array<{ questionNumber: string; verdict: string; fields: string[]; a?: string; b?: string }>;
  llm?: { provider?: string; inputTokens: number; outputTokens: number; costUsd?: number };
  modelInputIds?: string[];
  latencyMs?: number;
  teacherName?: string;
  teacherEmail?: string;
}

export interface QuestionSessionReportRunView {
  id: string;
  operation: 'questionSessionReport';
  status: 'running' | 'succeeded' | 'failed';
  userId: string;
  startedAt: string;
  completedAt?: string;
  error?: string;
  correlation?: { sessionId?: string; questionSetId?: string };
  asyncJobId?: string;
  llm?: { provider?: string; inputTokens: number; outputTokens: number; costUsd?: number };
  modelInputIds?: string[];
  modelOutputIds?: string[];
  latencyMs?: number;
  studentName?: string;
  studentEmail?: string;
}

/**
 * One-shot attempt-marking run — the durable record of marking one completed
 * attempt against its uploaded mark-scheme document.
 *
 * The counters are the point. This pipeline's failure mode is silent WRONGNESS,
 * not an exception: a misread scheme marks a real student against a fiction and
 * nothing throws. `tariffSum` vs `statedMaximumMark`, and the count of attempted
 * questions the model returned nothing for, are the only places that shows up
 * before someone disputes a grade.
 * See docs/FeatureDesigns/AssessmentAndMarking/one-shot-marking.md.
 */
/** One cohort misconception-clustering run. Partitioned by the TEACHER who ran
 *  it — the unit of work is a cohort they own, not any one student's data. */
export interface CohortClusterRunView {
  id: string;
  operation: 'clusterMisconceptions';
  status: 'running' | 'succeeded' | 'failed';
  userId: string;
  startedAt: string;
  completedAt?: string;
  error?: string;
  cohortId: string;
  subject: string;
  llm?: { provider?: string; inputTokens: number; outputTokens: number; costUsd?: number };
  modelInputIds?: string[];
  modelOutputIds?: string[];
  latencyMs?: number;
  teacherName?: string;
  teacherEmail?: string;
  /** Share of sent observations the kept clusters cover, 0-1. Computed server-side
   *  so every consumer reads it the same way. Undefined until the run completes. */
  coverage?: number;
  /** Present once the pass completes; absent on a run that died before the model
   *  returned — which is itself the signal. */
  outcome?: {
    observationsSent: number;
    groupsReturned: number;
    clustersKept: number;
    observationsClustered: number;
    unknownIds: number;
    duplicateIds: number;
  };
}

export interface MarkAttemptRunView {
  id: string;
  operation: 'markAttempt';
  status: 'running' | 'succeeded' | 'failed';
  userId: string;
  startedAt: string;
  completedAt?: string;
  error?: string;
  correlation?: { sessionId?: string; questionSetId?: string };
  asyncJobId?: string;
  llm?: { provider?: string; inputTokens: number; outputTokens: number; costUsd?: number };
  modelInputIds?: string[];
  modelOutputIds?: string[];
  latencyMs?: number;
  studentName?: string;
  studentEmail?: string;
  questionSetId?: string;
  schemeBlobPath?: string;
  schemeSourceKind?: 'pdf' | 'docx';
  /** Present once the pass completes; absent on a run that died before the model
   *  returned — which is itself the signal. One object, not a dozen fields:
   *  these numbers only mean anything together (`tariffSum` alone says nothing,
   *  `tariffSum` beside `statedMaximumMark` says whether the scheme was read
   *  correctly), and Cosmos caps a patch request at 10 operations. */
  outcome?: {
    schemePages?: number;
    questionsReturned: number;
    questionsMarked: number;
    questionsWithCredits: number;
    manualCredits: number;
    tariffSum: number;
    statedMaximumMark: number | null;
    totalsAgree: boolean;
    unreturnedIndices: number[];
    unmatchedIndices: number[];
    marksAwarded: number;
    maxMarks: number;
  };
}

/** Sentinel teacherId value meaning "all teachers in tenant". */
export const ALL_TEACHERS = '__all__';

// API Metrics types (kept in barrel since they belong to core)
export interface ApiMetricsEndpoint {
  url: string;
  callCount: number;
  avgRU: number;
  avgLatencyMs: number;
  totalRU: number;
  /** Hard CosmosDB 429s (SDK retries exhausted) attributed to this endpoint. */
  throttleCount: number;
  /** SDK diagnostics rollup; present only when a signal is non-zero. */
  sdk?: { retries: number; throttledRetries: number; metadataLookups: number };
  containers: Record<string, { opCount: number; avgRU: number; avgLatencyMs: number }>;
}

export interface ApiMetricsResponse {
  period: { from: string; to: string; hours: number };
  totalThrottles: number;
  totalAbsorbedThrottleRetries: number;
  endpoints: ApiMetricsEndpoint[];
}

export interface LlmMetricsTokens {
  input: number;
  output: number;
  reasoning: number;
}

export interface LlmMetricsGroup {
  operation: string;
  model: string;
  callCount: number;
  p50LatencyMs: number;
  p95LatencyMs: number;
  /** Fractions 0..1. */
  errorRate: number;
  rateLimitRate: number;
  failoverRate: number;
  avgCostUsd: number;
  totalCostUsd: number;
  tokens: LlmMetricsTokens;
  statusCounts: Record<string, number>;
}

export interface LlmMetricsResponse {
  period: { from: string; to: string; hours: number };
  totalCalls: number;
  overall: {
    p50LatencyMs: number;
    p95LatencyMs: number;
    errorRate: number;
    rateLimitRate: number;
    failoverRate: number;
    totalCostUsd: number;
    tokens: LlmMetricsTokens;
  };
  groups: LlmMetricsGroup[];
}

export interface PagePerfComponent {
  name: string;
  count: number;
  p50Ms: number;
  p95Ms: number;
  cacheRate: number; // 0..1
}

export interface PagePerfPage {
  /** Which portal these samples came from — 'web-teacher', 'web-student',
   *  'web-admin', or 'unknown' for rows recorded before it was stored. Route
   *  labels repeat across the three apps, so a page is always reported per
   *  portal. */
  source: string;
  page: string;
  loadCount: number;
  coldCount: number;
  p50Ms: number;
  p95Ms: number;
  /** Loads whose measurement hit the 30s cap — their duration is a floor. */
  cappedCount: number;
  components: PagePerfComponent[];
}

/** One user action (save/publish/…) on one page. `components` holds the
 *  request-vs-settle split — see useActionTiming. */
export interface PagePerfAction {
  /** @see PagePerfPage.source */
  source: string;
  page: string;
  action: string;
  count: number;
  failCount: number;
  cappedCount: number;
  p50Ms: number;
  p95Ms: number;
  components: PagePerfComponent[];
}

export interface PagePerfResponse {
  period: { from: string; to: string; hours: number };
  totalLoads: number;
  totalActions: number;
  pages: PagePerfPage[];
  actions: PagePerfAction[];
}

export interface ErrorStatRow {
  code: string;
  provider: string;
  stage: 'call' | 'parse';
  count: number;
}
export interface ErrorStatsResponse {
  windowDays: number;
  totalErrors: number;
  byCode: { code: string; count: number }[];
  rows: ErrorStatRow[];
}

export interface SessionIssue {
  kind: string;
  detectedAt: string;
  detail?: Record<string, unknown>;
}
export interface SessionIssueRow {
  sessionId: string;
  userId: string;
  user?: { id: string; name: string; email: string };
  questionSetTitle?: string;
  status: string;
  startedAt: string;
  endedAt?: string;
  issues?: SessionIssue[];
}
export interface SessionIssuesResponse {
  items: SessionIssueRow[];
  total: number;
  page: number;
  pageSize: number;
}

// Import the partial API objects
import { questionApi } from './questionApi';
import { modelApi } from './modelApi';
import { userApi } from './userApi';
import { curriculumApi } from './curriculumApi';
import { booksApi } from './booksApi';
import { schoolsApi } from './schoolsApi';
import { conceptsApi } from './conceptsApi';
import { platformExamApi } from './platformExamApi';

// ── Persona agents (dev-only synthetic users) — see docs/FeatureDesigns/persona-agents.md ──
export interface AgentRunPersona { personaId: string; status: string; feedbackCount?: number; sessionId?: string; rating?: number; }
export interface AgentRun {
  id: string; runId: string; trigger: string; status: string;
  roster: string[]; startedAt: string; endedAt?: string;
  tokenBudget?: number; tokensSpent?: number; costUsd?: number; perPersona?: AgentRunPersona[];
}
export interface AgentSessionStep {
  idx: number; at: string; intent?: string; resolution?: 'dom' | 'vision' | 'miss';
  screenshotRef?: string;
  toolCall?: { name: string; args?: Record<string, unknown> };
  toolResultSummary?: string; modelReasoning?: string;
}
export interface AgentSession {
  id: string; runId: string; personaId: string; app: string; status: string;
  startedAt: string; endedAt?: string; rating?: number; steps: AgentSessionStep[]; feedbackIds?: string[];
}
export type FindingSource = 'product' | 'harness' | 'agent-infra' | 'unknown';
export type FindingSubStatus = 'wont-fix' | 'duplicate' | 'not-reproducible' | 'by-design' | 'needs-info';
export interface FindingTriageEntry {
  at: string; by: string; status: string;
  source?: FindingSource; triageSeverity?: number; substatus?: FindingSubStatus; comment?: string;
}
export interface AgentFeedback {
  id: string; personaId: string; runId: string; sessionId?: string; app: string;
  operation?: string; category: string; severity: number; observation: string; expectation?: string;
  artifacts: Array<{ kind: string; ref: string }>; createdAt?: string; createdBy?: string;
  status?: string;
  // Human triage overlay (latest values; full trail in triageLog).
  source?: FindingSource; triageSeverity?: number; substatus?: FindingSubStatus;
  triageComment?: string; triageLog?: FindingTriageEntry[];
}
/** Attributes captured when triaging a finding — status is required, the rest optional. */
export interface FindingTriagePatch {
  status: string; source?: FindingSource; triageSeverity?: number;
  substatus?: FindingSubStatus; comment?: string;
}

// Analyst digests (Phase 3) — ranked, verified clusters of findings.
/** verdict = did it happen; provenance = whose fault. A toolbelt bug is `verified` +
 *  `harness-suspect`: real, but ours to fix, not the product's. */
export type AgentClusterProvenance = 'product' | 'harness-suspect' | 'agent-infra' | 'unknown';
export interface AgentDigestCluster {
  id: string; kind: 'issue' | 'praise'; theme: string; summary: string;
  app?: string; operation?: string; category: string;
  // severityWorst = the worst member's severity, i.e. the LOWEST number (1 = critical).
  frequency: number; severityWorst: number; severityAvg: number; rank: number;
  findingIds: string[]; sampleObservations: string[];
  verification: { verdict: 'verified' | 'unverified' | 'unchecked'; provenance: AgentClusterProvenance; note: string; evidence?: string[] };
}
export interface AgentDigest {
  id: string; generatedAt: string; generatedBy: string;
  windowStart: string; windowEnd: string;
  findingCount: number; clusteredCount: number;
  clusters: AgentDigestCluster[]; model?: string; notes?: string;
}

/** Editable persona spec (agent-personas — the single source of truth). */
export interface AgentPersonaSpec {
  id: string;
  app: string;
  hands: string;
  role: string;
  board: string;
  grade: number;
  subjects: string[];
  personality: string;
  device: string;
  skillLevel: string;
  patience: string;
  effort?: string;
  entry?: string;
  freVariants?: boolean;
  goalTemplate: string;
}

export interface AgentPersonaVersion {
  version: number;
  spec: AgentPersonaSpec;
  updatedBy: string;
  at: string;
}

export interface AgentPersonaRow {
  personaId: string;
  spec: Partial<AgentPersonaSpec>;
  runCount: number; firstRunAt?: string; lastRunAt?: string; findingsCount: number;
}

export interface AgentPersonaDetailDoc {
  spec: AgentPersonaSpec;
  version: number;
  updatedBy?: string;
  history?: AgentPersonaVersion[];
}

// Core APIs that stay in adminApi directly
const coreApi = {
  /** `includeAll` widens every user-scoped chart to dummies/guests/non-prod
   *  rings — the dashboard's audience toggle. Default counts real users only. */
  getStats: (days?: number, includeAll?: boolean) => {
    const p = new URLSearchParams();
    if (days) p.set('days', String(days));
    if (includeAll) p.set('includeAll', 'true');
    const qs = p.toString();
    return fetchWithAdmin<AdminStats>(`/console/stats${qs ? `?${qs}` : ''}`);
  },

  getThemeStats: (days?: number, includeAll?: boolean) => {
    const p = new URLSearchParams();
    if (days) p.set('days', String(days));
    if (includeAll) p.set('includeAll', 'true');
    const qs = p.toString();
    return fetchWithAdmin<ThemeStats>(`/system/theme-stats${qs ? `?${qs}` : ''}`);
  },

  getRetentionStats: (days?: number, includeAll?: boolean, segment?: RetentionSegment) => {
    const p = new URLSearchParams();
    if (days) p.set('days', String(days));
    if (includeAll) p.set('includeAll', 'true');
    setSegment(p, segment);
    const qs = p.toString();
    return fetchWithAdmin<RetentionStats>(`/console/retention${qs ? `?${qs}` : ''}`);
  },

  getCohortUsers: (start: string, index: number, includeAll?: boolean, grain: 'week' | 'month' = 'week', segment?: RetentionSegment) => {
    const p = grain === 'month'
      ? new URLSearchParams({ month: start, monthIndex: String(index) })
      : new URLSearchParams({ week: start, weekIndex: String(index) });
    if (includeAll) p.set('includeAll', 'true');
    setSegment(p, segment);
    return fetchWithAdmin<CohortUsers>(`/console/retention/cohort-users?${p.toString()}`);
  },

  // Persona agents observability
  getAgentRunDetail: (runId: string) =>
    fetchWithAdmin<{ run: AgentRun | null; sessions: AgentSession[]; feedback: AgentFeedback[] }>(
      `/system/agents/runs/${encodeURIComponent(runId)}`,
    ),
  getAgentScreenshot: (ref: string) =>
    fetchWithAdmin<{ dataUri: string }>(`/system/agents/screenshot?ref=${encodeURIComponent(ref)}`),
  getAgentPersonas: () => fetchWithAdmin<{ personas: AgentPersonaRow[] }>(`/system/agents/personas`),
  getAgentPersona: (personaId: string) =>
    fetchWithAdmin<{ persona: AgentPersonaDetailDoc | null; runs: AgentRun[]; findingsCount: number }>(
      `/system/agents/personas/${encodeURIComponent(personaId)}`,
    ),
  createAgentPersona: (spec: AgentPersonaSpec) =>
    fetchWithAdmin<{ persona: AgentPersonaDetailDoc }>(`/system/agents/personas`, {
      method: 'POST',
      body: JSON.stringify({ spec }),
    }),
  updateAgentPersona: (personaId: string, spec: AgentPersonaSpec) =>
    fetchWithAdmin<{ persona: AgentPersonaDetailDoc }>(`/system/agents/personas/${encodeURIComponent(personaId)}`, {
      method: 'PUT',
      body: JSON.stringify({ spec }),
    }),
  getAgentFindings: (filters?: { persona?: string; operation?: string; category?: string; status?: string; source?: string; maxSeverity?: number }) => {
    const sp = new URLSearchParams();
    if (filters?.persona) sp.set('persona', filters.persona);
    if (filters?.operation) sp.set('operation', filters.operation);
    if (filters?.category) sp.set('category', filters.category);
    if (filters?.status) sp.set('status', filters.status);
    if (filters?.source) sp.set('source', filters.source);
    // SEV1-style: maxSeverity=N keeps findings 1..N (at least this severe).
    if (filters?.maxSeverity) sp.set('maxSeverity', String(filters.maxSeverity));
    const q = sp.toString();
    return fetchWithAdmin<{ findings: AgentFeedback[] }>(`/system/agents/findings${q ? `?${q}` : ''}`);
  },
  /** Apply a triage action: status move + optional attribution (source/severity/substatus/comment). */
  triageAgentFinding: (id: string, personaId: string, patch: FindingTriagePatch) =>
    fetchWithAdmin<{ ok: boolean }>(`/system/agents/findings/${encodeURIComponent(id)}/status`, {
      method: 'POST',
      body: JSON.stringify({ personaId, ...patch }),
    }),
  // Analyst digests (Phase 3)
  getAgentDigest: () => fetchWithAdmin<{ digest: AgentDigest | null }>(`/system/agents/digest`),
  generateAgentDigest: (windowDays?: number) =>
    fetchWithAdmin<{ digest: AgentDigest }>(`/system/agents/digest/generate`, {
      method: 'POST',
      body: JSON.stringify(windowDays ? { windowDays } : {}),
    }),
  getAgentRuns: (filters?: { limit?: number; persona?: string; status?: string; search?: string }) => {
    const sp = new URLSearchParams();
    if (filters?.limit) sp.set('limit', String(filters.limit));
    if (filters?.persona) sp.set('persona', filters.persona);
    if (filters?.status) sp.set('status', filters.status);
    if (filters?.search) sp.set('search', filters.search);
    const q = sp.toString();
    return fetchWithAdmin<{ runs: AgentRun[] }>(`/system/agents/runs${q ? `?${q}` : ''}`);
  },

  getTeacherStats: (teacherId: string, days: 7 | 28 | 56, tenantId?: string, includeAll?: boolean) => {
    const params = new URLSearchParams({ teacherId, days: String(days) });
    if (tenantId) params.set('tenantId', tenantId);
    if (includeAll) params.set('includeAll', 'true');
    return fetchWithAdmin<TeacherStatsResponse>(`/console/teacher-stats?${params.toString()}`);
  },

  /** Solve Sources tab — per-student breakdown by source or by who assigned.
   *  See docs/FeatureDesigns/question-source-breakdown.md. */
  getQuestionSources: (params: {
    days: number;
    axis: 'source' | 'grant';
    metric: 'questions' | 'attempts';
    page: number;
    pageSize: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
    rings?: string;
    includeDummies?: boolean;
    includeAgents?: boolean;
    /** Page-level audience toggle — widens the synthetic filters above. */
    includeAll?: boolean;
  }) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '') q.set(k, String(v));
    });
    return fetchWithAdmin<QuestionSourcesResponse>(`/console/analytics/question-sources?${q}`);
  },

  /** LLM spend for the Costs tab. `days` is capped server-side at 28 — the
   *  llm-metrics container has a 30-day TTL, so there is no older data.
   *  See Backend/functions/src/admin/analytics/getCostAnalytics.ts. */
  getCostAnalytics: (params: { days: number; includeAll?: boolean }) => {
    const q = new URLSearchParams();
    q.set('days', String(params.days));
    if (params.includeAll) q.set('includeAll', 'true');
    return fetchWithAdmin<CostAnalyticsResponse>(`/system/cost-analytics?${q}`);
  },

  getQuestionStats: (status?: string) => fetchWithAdmin<{
    periods: Array<{
      period: string;
      label: string;
      questionsAdded: Array<{ subject: string; count: number }>;
      questionsAddedTotal: number;
      uniqueQuestionsAttempted: Array<{ subject: string; count: number }>;
      uniqueQuestionsAttemptedTotal: number;
    }>;
  }>(`/console/question-stats${status ? `?status=${status}` : ''}`),

  getQuestionStatsTimeseries: (opts?: { status?: string; days?: number }) => {
    const params = new URLSearchParams();
    if (opts?.status) params.set('status', opts.status);
    if (opts?.days) params.set('days', String(opts.days));
    const qs = params.toString();
    return fetchWithAdmin<{
      buckets: Array<{ date: string; maths: number; physics: number; chemistry: number }>;
    }>(`/console/question-stats/timeseries${qs ? `?${qs}` : ''}`);
  },

  getSubmissions: (params?: {
    page?: number;
    pageSize?: number;
    userId?: string;
    questionId?: string;
    subject?: string;
    scoreRange?: string;
    platform?: string;
    userFeedback?: string;
    email?: string;
    ring?: string;
    agents?: string;
    sinceDate?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.userId) searchParams.set('userId', params.userId);
    if (params?.questionId) searchParams.set('questionId', params.questionId);
    if (params?.subject) searchParams.set('subject', params.subject);
    if (params?.scoreRange) searchParams.set('scoreRange', params.scoreRange);
    if (params?.platform) searchParams.set('platform', params.platform);
    if (params?.userFeedback) searchParams.set('userFeedback', params.userFeedback);
    if (params?.email) searchParams.set('email', params.email);
    if (params?.ring) searchParams.set('ring', params.ring);
    if (params?.agents) searchParams.set('agents', params.agents);
    if (params?.sinceDate) searchParams.set('sinceDate', params.sinceDate);

    const query = searchParams.toString();
    return fetchWithAdmin<PaginatedResponse<Submission>>(
      `/console/solution-analyses${query ? `?${query}` : ''}`
    );
  },

  getSubmission: (id: string) => fetchWithAdmin<Submission>(`/console/solution-analyses/${id}`),

  getSubmissionStrokeData: (id: string) => fetchWithAdmin<{
    /** Preferred (post-cutover): public blob URL holding `{ canvasJSON, strokeTimeline }`.
     *  The client fetches it directly — see canvas-json-blob-storage.md. */
    canvasJsonUrl: string | null;
    /** Legacy inline fields — only set when canvasJsonUrl is null. */
    canvasJSON: string | null;
    strokeTimeline: Array<{
      type: 'stroke' | 'erase' | 'undo' | 'redo' | 'clear';
      timestamp: number;
      elapsed: number;
      index: number;
      bbox?: { left: number; top: number; width: number; height: number };
    }> | null;
  }>(`/console/solution-analyses/${id}/stroke-data`),

  getHintRequests: (params?: {
    page?: number;
    pageSize?: number;
    subject?: string;
    status?: string;
    email?: string;
    userId?: string;
    agents?: string;
    sinceDate?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.subject) searchParams.set('subject', params.subject);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.email) searchParams.set('email', params.email);
    if (params?.userId) searchParams.set('userId', params.userId);
    if (params?.agents) searchParams.set('agents', params.agents);
    if (params?.sinceDate) searchParams.set('sinceDate', params.sinceDate);

    const query = searchParams.toString();
    return fetchWithAdmin<{
      hintRequests: HintRequest[];
      total: number;
      page: number;
      pageSize: number;
    }>(`/console/hints${query ? `?${query}` : ''}`);
  },

  getHintRequest: (id: string) => fetchWithAdmin<HintRequest>(`/console/hints/${id}`),

  // Solution split (solution-splitter pipeline) admin views.
  getSolutionSplits: (params?: {
    page?: number; pageSize?: number; status?: string;
    email?: string; userId?: string; agents?: string; sinceDate?: string;
  }) => {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', params.page.toString());
    if (params?.pageSize) sp.set('pageSize', params.pageSize.toString());
    if (params?.status) sp.set('status', params.status);
    if (params?.email) sp.set('email', params.email);
    if (params?.userId) sp.set('userId', params.userId);
    if (params?.agents) sp.set('agents', params.agents);
    if (params?.sinceDate) sp.set('sinceDate', params.sinceDate);
    const query = sp.toString();
    return fetchWithAdmin<PaginatedSolutionSplits>(`/console/solution-splits${query ? `?${query}` : ''}`);
  },

  getSolutionSplit: (id: string) => fetchWithAdmin<SolutionSplitDetail>(`/console/solution-splits/${id}`),

  getConceptLabRequests: (params?: {
    page?: number;
    pageSize?: number;
    provider?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.provider) searchParams.set('provider', params.provider);

    const query = searchParams.toString();
    return fetchWithAdmin<{
      conceptLabEntries: ConceptLabEntry[];
      total: number;
      page: number;
      pageSize: number;
    }>(`/console/concept-lab${query ? `?${query}` : ''}`);
  },

  getConceptLabRequest: (id: string) => fetchWithAdmin<ConceptLabEntry>(`/console/concept-lab/${id}`),

  /** Operations → Learning Moments: one row per detect-learning-moments run. */
  getLearningMomentRuns: (params?: {
    page?: number;
    pageSize?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const query = searchParams.toString();
    return fetchWithAdmin<{
      runs: LearningMomentRun[];
      total: number;
      page: number;
      pageSize: number;
    }>(`/console/learning-moment-runs${query ? `?${query}` : ''}`);
  },

  /** Reject one learning moment on a submission (`rejected: false` restores).
   *  The analysis and the mark are untouched. See moment-rejection-target.md. */
  rejectLearningMoment: (submissionId: string, learningMomentId: string, rejected: boolean, comment?: string, why?: AdjustmentWhyValue) =>
    fetchWithAdmin<{ submissionId: string; learningMomentId: string; changed: number }>(
      `/solution-analyses/${submissionId}/moments/${encodeURIComponent(learningMomentId)}/reject`,
      { method: 'POST', body: JSON.stringify({ rejected, comment, ...why }) },
    ),

  submitAdminFeedback: (submissionId: string, rating: 'positive' | 'negative', comment?: string) =>
    fetchWithAdmin<{ success: boolean; submissionId: string; autoImportTarget: string }>(
      `/console/solution-analyses/${submissionId}/feedback`,
      {
        method: 'POST',
        body: JSON.stringify({ rating, comment }),
      }
    ),

  overrideScores: (submissionId: string, scores: AdjustmentWhyValue & {
    correctnessScore?: number;
    correctnessScoreComment?: string;
    approachScore?: number;
    approachScoreComment?: string;
    speedScore?: number;
    speedScoreComment?: string;
  }) =>
    fetchWithAdmin<{
      scoreDiff: {
        correctnessScore: { old?: number; new?: number };
        speedScore: { old?: number; new?: number };
        approachScore: { old?: number; new?: number };
        finalScore: { old?: number; new?: number };
      };
      assessmentVersion: number;
    }>('/teacher/override-scores', {
      method: 'POST',
      body: JSON.stringify({ submissionId, ...scores }),
    }),

  /** `note` — what the admin believes the first reading got wrong; passed to the
   *  readers and analyzers of the re-run. Ungated for admins.
   *  See docs/FeatureDesigns/solution-analysis/reevaluation-note.md. */
  /** Runs as a background job (it can take minutes, and must survive the
   *  server instance): queue it, then wait for the job's result. The job is
   *  the admin's own, so the user-facing /async-jobs/{id} reads it.
   *  See docs/FeatureDesigns/solution-analysis/reevaluation-job-target.md. */
  reEvaluate: async (submissionId: string, note?: string, why?: AdjustmentWhyValue) => {
    const { jobId } = await fetchWithAdmin<{ jobId: string }>('/reevaluate', {
      method: 'POST',
      body: JSON.stringify({ submissionId, ...(note?.trim() ? { note: note.trim() } : {}), ...why, asJob: true }),
    });
    return waitForAsyncJob<{
      newSubmissionId: string;
      originalSubmissionId: string;
      feedback: any;
      scoreDiff: {
        correctnessScore: { old?: number; new?: number };
        speedScore: { old?: number; new?: number };
        approachScore: { old?: number; new?: number };
        finalScore: { old?: number; new?: number };
      };
      assessmentVersion: number;
    }>(id => fetchWithAdmin<{ job: PolledJobView | null }>(`/async-jobs/${encodeURIComponent(id)}`), jobId);
  },

  // Settings
  getSettings: () => fetchWithAdmin<AppSettings>('/console/settings'),

  updateSettings: async (settings: Partial<AppSettings>): Promise<AppSettings> => {
    const token = authApi.getToken();

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/console/settings`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data: ApiResponse<AppSettings> = await response.json();

    if (!data.success) {
      throw new Error(getErrorMessage(data.error) || 'Unknown error');
    }

    return data.data as AppSettings;
  },

  // Providers
  getAvailableProviders: () =>
    fetchWithAdmin<Record<string, boolean>>('/console/providers/available'),

  getAvailableProvidersDetailed: () =>
    fetchWithAdmin<{ key: string; displayName: string; modelName: string; family: string; available: boolean }[]>('/console/providers/available-detailed'),

  // ── System introspection (/system/*) ──────────────────────────────
  // Deployment/runtime info — new admin-gated system endpoints live under
  // /system/*, not the legacy /console/* prefix.

  /** Names (never values) of the API-key env vars set on the function app,
   *  keyed by provider family. Drives the apiKeyEnv dropdown. */
  getApiKeyEnvs: () =>
    fetchWithAdmin<Record<string, string[]>>('/system/llm/api-key-envs'),

  /** Model ids available from the vendor for a family (via its list-models
   *  API). Empty list when the key is missing or the vendor call fails —
   *  callers fall back to free-text entry. */
  getProviderModels: (family: string) =>
    fetchWithAdmin<{ family: string; models: string[] }>(`/system/llm/models?family=${encodeURIComponent(family)}`),

  // List all providers — returns the same shape as the legacy doc for caller convenience
  getProviderConfig: async (): Promise<ProviderConfigDoc> => {
    const result = await fetchWithAdmin<{ providers: ProviderConfig[] }>('/console/providers');
    return {
      id: 'provider-config',
      providers: result.providers,
      updatedAt: new Date().toISOString(),
    };
  },

  // Bulk replace — atomic full-array update used by the Providers admin tab
  updateProviderConfig: (providers: ProviderConfig[]) =>
    fetchWithAdmin<ProviderConfigDoc>('/console/providers-bulk', {
      method: 'PUT',
      body: JSON.stringify({ providers }),
    }),

  // Per-row REST CRUD (for finer-grained operations)
  createProvider: (provider: ProviderConfig) =>
    fetchWithAdmin<ProviderConfig>('/console/providers', {
      method: 'POST',
      body: JSON.stringify(provider),
    }),

  updateProvider: (key: string, updates: Partial<ProviderConfig>) =>
    fetchWithAdmin<ProviderConfig>(`/console/providers/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),

  deleteProvider: (key: string) =>
    fetchWithAdmin<{ ok: boolean; deleted: string }>(`/console/providers/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    }),

  // Tiered provider sets (analysis tier routing)
  listProviderTieredSets: () =>
    fetchWithAdmin<{ sets: ProviderTieredSet[] }>('/console/provider-tiered-sets'),

  createProviderTieredSet: (set: ProviderTieredSet) =>
    fetchWithAdmin<ProviderTieredSet>('/console/provider-tiered-sets', {
      method: 'POST',
      body: JSON.stringify(set),
    }),

  updateProviderTieredSet: (name: string, set: ProviderTieredSet) =>
    fetchWithAdmin<ProviderTieredSet>(`/console/provider-tiered-sets/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: JSON.stringify(set),
    }),

  deleteProviderTieredSet: (name: string) =>
    fetchWithAdmin<{ ok: boolean; deleted: string }>(`/console/provider-tiered-sets/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    }),

  // Parallel provider sets (fan-out branches referenced from tier slots)
  listProviderParallelSets: () =>
    fetchWithAdmin<{ sets: ProviderParallelSet[] }>('/console/provider-parallel-sets'),

  createProviderParallelSet: (set: ProviderParallelSet) =>
    fetchWithAdmin<ProviderParallelSet>('/console/provider-parallel-sets', {
      method: 'POST',
      body: JSON.stringify(set),
    }),

  updateProviderParallelSet: (name: string, set: ProviderParallelSet) =>
    fetchWithAdmin<ProviderParallelSet>(`/console/provider-parallel-sets/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: JSON.stringify(set),
    }),

  deleteProviderParallelSet: (name: string) =>
    fetchWithAdmin<{ ok: boolean; deleted: string }>(`/console/provider-parallel-sets/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    }),

  // Pipeline configs (per-config docs in CosmosDB)
  listPipelineConfigs: () =>
    fetchWithAdmin<{ configs: { name: string; updatedAt: string }[] }>('/console/pipeline-configs'),

  getPipelineConfig: (name: string) =>
    fetchWithAdmin<PipelineConfigDoc>(`/console/pipeline-configs/${encodeURIComponent(name)}`),

  createPipelineConfig: (name: string, operations?: OperationsMap) =>
    fetchWithAdmin<PipelineConfigDoc>('/console/pipeline-configs', {
      method: 'POST',
      body: JSON.stringify({ name, operations: operations || {} }),
    }),

  updatePipelineConfig: (name: string, operations: OperationsMap) =>
    fetchWithAdmin<PipelineConfigDoc>(`/console/pipeline-configs/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: JSON.stringify({ operations }),
    }),

  deletePipelineConfig: (name: string) =>
    fetchWithAdmin<{ ok: boolean; deleted: string }>(`/console/pipeline-configs/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    }),

  getPromptPipelineUsage: (promptId: string) =>
    fetchWithAdmin<{ configs: { name: string; operations: string[] }[] }>(
      `/console/prompts/${encodeURIComponent(promptId)}/pipeline-usage`
    ),

  getPipelineConfigRevisions: (name: string) =>
    fetchWithAdmin<{ revisions: PipelineConfigRevisionSummary[] }>(
      `/console/pipeline-configs/${encodeURIComponent(name)}/revisions`
    ),

  getPipelineConfigRevision: (name: string, seq: number) =>
    fetchWithAdmin<PipelineConfigRevision>(
      `/console/pipeline-configs/${encodeURIComponent(name)}/revisions/${seq}`
    ),

  restorePipelineConfigRevision: (name: string, seq: number) =>
    fetchWithAdmin<PipelineConfigDoc>(
      `/console/pipeline-configs/${encodeURIComponent(name)}/revisions/${seq}/restore`,
      { method: 'POST' }
    ),

  // Dashboard performance
  getPerformanceStats: () =>
    fetchWithAdmin<{
      byPrompt: Array<{
        promptId: string;
        promptName: string;
        totalEvaluations: number;
        avgAccuracy: number;
        avgFeedbackQuality: number;
        avgCompleteness: number;
        avgOverall: number;
      }>;
      byProvider: Array<{
        provider: string;
        totalEvaluations: number;
        avgAccuracy: number;
        avgFeedbackQuality: number;
        avgCompleteness: number;
        avgOverall: number;
      }>;
      totalEvaluations: number;
    }>('/console/performance'),

  // Pipeline observability (execution health, sliced by prompt × model × provider)
  getPipelineMetrics: (params?: { from?: string; to?: string; source?: string; promptId?: string; model?: string }) => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    if (params?.source) qs.set('source', params.source);
    if (params?.promptId) qs.set('promptId', params.promptId);
    if (params?.model) qs.set('model', params.model);
    const q = qs.toString();
    return fetchWithAdmin<PipelineMetrics>(`/system/pipeline-metrics${q ? `?${q}` : ''}`);
  },

  /** Rows behind one quality-check row. Same window/source as the table, so the
   *  totals reconcile with the number that was clicked. */
  getQualityCheckDrilldown(name: string, opts: { from?: string; to?: string; source?: string } = {}) {
    const p = new URLSearchParams();
    if (opts.from) p.set('from', opts.from);
    if (opts.to) p.set('to', opts.to);
    if (opts.source) p.set('source', opts.source);
    const q = p.toString();
    return fetchWithAdmin<QualityCheckDrilldown>(
      `/system/pipeline-metrics/checks/${encodeURIComponent(name)}${q ? `?${q}` : ''}`,
    );
  },

  /** Rule on a suspect answer key: `accepted` = the key was wrong, `dismissed`
   *  = the key is right and the model was not, `open` = reopen for review.
   *  Records the ruling only — correcting the key stays the question-edit
   *  path, where it is attributed and reviewable. */
  resolveSuspectAnswerKey: (questionId: string, status: 'accepted' | 'dismissed' | 'open') =>
    fetchWithAdmin<{ questionId: string; status: string }>(
      `/system/questions/${encodeURIComponent(questionId)}/suspect-answer-key`,
      { method: 'POST', body: JSON.stringify({ status }) },
    ),

  getLifecycleFunnel: (params?: {
    cohortWeek?: string; experimentKey?: string; sliceBy?: string; segmentId?: string; windowDays?: number;
    /** A published link's people — its first touch — instead of a segment. */
    link?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params?.link) qs.set('link', params.link);
    if (params?.cohortWeek) qs.set('cohortWeek', params.cohortWeek);
    if (params?.experimentKey) qs.set('experimentKey', params.experimentKey);
    if (params?.sliceBy) qs.set('sliceBy', params.sliceBy);
    if (params?.segmentId) qs.set('segmentId', params.segmentId);
    if (params?.windowDays) qs.set('window', String(params.windowDays));
    const q = qs.toString();
    return fetchWithAdmin<LifecycleFunnel>(`/system/lifecycle-funnel${q ? `?${q}` : ''}`);
  },

  // Logs
  getLogs: (params?: {
    date?: string;
    level?: string;
    component?: string;
    requestId?: string;
    submissionId?: string;
    source?: string;
    userEmail?: string;
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.date) searchParams.set('date', params.date);
    if (params?.level) searchParams.set('level', params.level);
    if (params?.component) searchParams.set('component', params.component);
    if (params?.requestId) searchParams.set('requestId', params.requestId);
    if (params?.submissionId) searchParams.set('submissionId', params.submissionId);
    if (params?.source) searchParams.set('source', params.source);
    if (params?.userEmail) searchParams.set('userEmail', params.userEmail);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    const query = searchParams.toString();
    return fetchWithAdmin<{
      logs: Array<{
        id: string;
        pk: string;
        requestId: string;
        level: string;
        levelNum: number;
        component: string;
        componentCode: number;
        operation?: string;
        message: string;
        data?: Record<string, unknown>;
        error?: {
          code: number;
          word: string;
          message: string;
          context?: Record<string, unknown>;
        };
        userId?: string;
        userEmail?: string;
        submissionId?: string;
        duration?: number;
        source?: string;
        timestamp: string;
      }>;
      total: number;
      hasMore: boolean;
    }>(`/console/logs${query ? `?${query}` : ''}`);
  },

  clearLogs: async (): Promise<{ deleted: number }> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/console/logs`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<{ deleted: number }> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || 'Failed to clear logs');
    }
    return data.data as { deleted: number };
  },

  // Notification deliveries (Messages)
  getNotificationDeliveries: (params?: {
    channel?: string;
    status?: string;
    priority?: string;
    eventType?: string;
    recipientEmail?: string;
    recipientPhone?: string;
    userId?: string;
    userEmail?: string;
    intentId?: string;
    eventId?: string;
    templateUsed?: string;
    after?: string;
    before?: string;
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.channel) searchParams.set('channel', params.channel);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.priority) searchParams.set('priority', params.priority);
    if (params?.eventType) searchParams.set('eventType', params.eventType);
    if (params?.recipientEmail) searchParams.set('recipientEmail', params.recipientEmail);
    if (params?.recipientPhone) searchParams.set('recipientPhone', params.recipientPhone);
    if (params?.userId) searchParams.set('userId', params.userId);
    if (params?.userEmail) searchParams.set('userEmail', params.userEmail);
    if (params?.intentId) searchParams.set('intentId', params.intentId);
    if (params?.eventId) searchParams.set('eventId', params.eventId);
    if (params?.templateUsed) searchParams.set('templateUsed', params.templateUsed);
    if (params?.after) searchParams.set('after', params.after);
    if (params?.before) searchParams.set('before', params.before);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    const query = searchParams.toString();
    return fetchWithAdmin<{
      deliveries: Array<{
        id: string;
        partitionKey: string;
        intentId: string;
        eventId: string;
        eventType?: string;
        ruleId: string;
        recipient: { userId?: string; phone?: string; email?: string };
        /** User account email (looked up server-side via recipient.userId). */
        userEmail?: string;
        channel: 'whatsapp' | 'email' | 'inApp' | 'sms';
        priority: 'critical' | 'high' | 'normal' | 'low';
        templateUsed: string;
        renderedContent: Record<string, unknown>;
        status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | 'suppressed';
        suppressionReason?: 'muted' | 'quiet_hours' | 'no_contact' | 'no_template' | 'throttled' | 'unsubscribed' | 'not_rolled_out';
        providerMessageId?: string;
        attempts: number;
        lastError?: string;
        readAt?: string;
        createdAt: string;
        sentAt?: string;
        deliveredAt?: string;
      }>;
      hasMore: boolean;
    }>(`/console/notifications/deliveries${query ? `?${query}` : ''}`);
  },

  // Explorer
  getExplorerDocument: (container: string, id: string) =>
    fetchWithAdmin<{ container: string; id: string; document: Record<string, unknown> | null }>(
      `/console/explorer?container=${encodeURIComponent(container)}&id=${encodeURIComponent(id)}`
    ),

  getExplorerReferences: (sourceContainer: string, sourceField: string, value: string, isArray: boolean) =>
    fetchWithAdmin<{ sourceContainer: string; sourceField: string; ids: string[] }>(
      `/console/explorer/references?sourceContainer=${encodeURIComponent(sourceContainer)}&sourceField=${encodeURIComponent(sourceField)}&value=${encodeURIComponent(value)}&isArray=${isArray}`
    ),

  getExplorerUserTagReferences: (itemId: string, itemType: 'question' | 'submission') =>
    fetchWithAdmin<{ tags: { id: string; userId: string; tag: string }[] }>(
      `/console/explorer/user-tag-references?itemId=${encodeURIComponent(itemId)}&itemType=${itemType}`
    ),

  // API Metrics
  getApiMetrics: (hours: number) =>
    fetchWithAdmin<ApiMetricsResponse>(`/console/api-metrics?hours=${hours}`),

  getLlmMetrics: (hours: number) =>
    fetchWithAdmin<LlmMetricsResponse>(`/system/llm-metrics?hours=${hours}`),

  getPagePerf: (hours: number) =>
    fetchWithAdmin<PagePerfResponse>(`/system/page-perf?hours=${hours}`),

  // LLM error stats (taxonomy aggregation)
  getErrorStats: (days: number) =>
    fetchWithAdmin<ErrorStatsResponse>(`/console/error-stats?days=${days}`),

  getSessionIssues: (params?: { page?: number; pageSize?: number; kind?: string }) => {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', params.page.toString());
    if (params?.pageSize) sp.set('pageSize', params.pageSize.toString());
    if (params?.kind) sp.set('kind', params.kind);
    const query = sp.toString();
    return fetchWithAdmin<SessionIssuesResponse>(`/console/session-issues${query ? `?${query}` : ''}`);
  },

  // Config Sync
  exportConfig: (sections?: ConfigSectionName[]) => {
    const query = sections?.length ? `?sections=${sections.join(',')}` : '';
    return fetchWithAdmin<ConfigBundle>(`/console/config/export${query}`);
  },

  importConfigDryRun: (bundle: ConfigBundle) =>
    fetchWithAdmin<ConfigImportDryRunResult>('/console/config/import?dryRun=true', {
      method: 'POST',
      body: JSON.stringify(bundle),
    }),

  importConfigApply: (request: ConfigImportRequest) =>
    fetchWithAdmin<ConfigImportApplyResult>('/console/config/import', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  /** Drive backups — the user-dyninfo projection, never the connection docs
   *  (those hold sealed refresh tokens). See
   *  docs/FeatureDesigns/data-ownership/personal-drive-backup-target.md. */
  getDriveBackups: () =>
    fetchWithAdmin<{
      backups: {
        userId: string; name?: string; email?: string; status: string;
        accountEmail?: string; connectedAt: string; lastSweepAt?: string;
        lastDeliveredAt?: string; deliveredCount: number; problem?: string;
        stalled: boolean;
      }[];
    }>('/console/drive-backups'),

  /** One owner's deliveries, filtered on the server. `userId` is whose work
   *  (a student, for a teacher's copy); `from`/`to` are delivered dates. */
  getDriveBackupDeliveries: (ownerId: string, filters: {
    q?: string; type?: string; userId?: string; from?: string; to?: string;
  } = {}) => {
    const qs = new URLSearchParams(
      Object.entries(filters).filter((e): e is [string, string] => !!e[1]));
    return fetchWithAdmin<{
      deliveries: {
        artifactKey: string; category: string; fileType: string;
        subjectUserId?: string; subjectName?: string; driveName: string;
        driveFolderPath?: string; bytes: number; blobPath?: string; blobCreatedAt?: string; deliveredAt: string;
      }[];
    }>(`/console/drive-backups/${encodeURIComponent(ownerId)}${qs.size ? `?${qs}` : ''}`);
  },

  /** The bytes one delivery was copied from, resolved server-side through the
   *  delivery row — never by a path we send. */
  getDriveBackupSource: (ownerId: string, artifactKey: string) =>
    fetchWithAdmin<{ contentType: string; dataBase64: string }>(
      `/console/drive-backups/${encodeURIComponent(ownerId)}/source?artifactKey=${encodeURIComponent(artifactKey)}`),

  // Async-job monitoring (see docs/FeatureDesigns/async-jobs.md)
  getAsyncJobs: (params?: {
    status?: string;        // comma-separated AsyncJobStatus list
    type?: string;
    /** Resolved server-side via db.users.findByEmail. */
    userEmail?: string;
    correlationObjectType?: string;
    correlationObjectId?: string;
    since?: string;         // ISO; default: server picks last 24h
    until?: string;         // ISO
    limit?: number;
    offset?: number;
  }) => {
    const sp = new URLSearchParams();
    if (params?.status) sp.set('status', params.status);
    if (params?.type) sp.set('type', params.type);
    if (params?.userEmail) sp.set('userEmail', params.userEmail);
    if (params?.correlationObjectType) sp.set('correlationObjectType', params.correlationObjectType);
    if (params?.correlationObjectId) sp.set('correlationObjectId', params.correlationObjectId);
    if (params?.since) sp.set('since', params.since);
    if (params?.until) sp.set('until', params.until);
    if (params?.limit !== undefined) sp.set('limit', String(params.limit));
    if (params?.offset !== undefined) sp.set('offset', String(params.offset));
    const query = sp.toString();
    return fetchWithAdmin<{
      items: Array<{
        id: string;
        userId: string;
        type: string;
        status: 'queued' | 'running' | 'completed' | 'failed' | 'dead';
        attempts: number;
        maxAttempts: number;
        queuedAt: string;
        startedAt?: string;
        completedAt?: string;
        nextAttemptAt?: string;
        claimedBy?: string;
        correlationObjectType?: string;
        correlationObjectId?: string;
        metadata?: Record<string, string>;
        dedupKey?: string;
        lastError?: { message: string; type?: string; attemptedAt: string };
        latencyMs?: number;
        // Enrichment (present when resolvable): owner + generation-run details.
        teacherName?: string;
        teacherEmail?: string;
        generatedCount?: number;
        sourcePdfUrl?: string;
        /** What seeded a generation run, in words (PDF / selected questions /
         *  a student's mistakes). */
        sourceLabel?: string;
        /** The question set the run was filling, when it had one. */
        questionSetId?: string;
      }>;
      hasMore: boolean;
      counts: { byStatus: Record<string, number>; byType: Record<string, number>; byCorrelationType: Record<string, number> };
      appliedSince?: string;
      appliedUntil?: string;
    }>(`/console/async-jobs${query ? `?${query}` : ''}`);
  },

  /** Question-extraction runs — the DEDICATED run container, not async-jobs.
   *  The job record is a generic 7-day execution trail; this is the durable
   *  record of what an import read and what it produced.
   *  See docs/FeatureDesigns/operation-run-records.md. */
  getQuestionExtractionRuns: (params?: { status?: string; sourceKind?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.sourceKind) query.set('sourceKind', params.sourceKind);
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return fetchWithAdmin<{ items: QuestionExtractionRunView[] }>(
`/console/question-extraction-runs${qs ? `?${qs}` : ''}`);
  },

  /** Gold-set extraction eval runs, grouped by label, with the spread across
   *  repeats. The BENCH half of Evals -> Extraction; the production half is
   *  `getQuestionExtractionRuns` above. Both are shown together because a bench
   *  score with no production runs is a lab result, and production runs with no
   *  bench are anecdotes. */
  getExtractionEvals: () =>
    fetchWithAdmin<{ labels: ExtractionEvalLabel[] }>('/console/extraction-evals'),

  /** Drill-down for one gold document under one label: every repeat, with the
   *  per-question dimension breakdown and the expected-vs-actual text on each
   *  mismatch. This is the view a prompt or pipeline fix is written from. */
  getExtractionEvalDocument: (label: string, documentId: string) =>
    fetchWithAdmin<ExtractionEvalDetail>(
      `/console/extraction-evals/${encodeURIComponent(label)}/${encodeURIComponent(documentId)}`),

  /** One extraction run, by run id (cross-partition read). */
  getQuestionExtractionRun: (id: string) =>
    fetchWithAdmin<{ run: QuestionExtractionRunView }>(`/console/question-extraction-runs/${id}`),

  /** Assignments & Tests — every question set in the system, with the counts
   *  that say whether anyone has worked on it. Platform-auth; see
   *  Backend/functions/src/admin/content/adminQuestionSetSearch.ts. */
  searchQuestionSets: (body: AdminQuestionSetSearchArgs) =>
    fetchWithAdmin<{ rows: AdminQuestionSetListRow[]; total: number }>('/console/question-sets/search', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** The values that actually occur in the corpus, for the list's filters —
   *  asked of the data rather than hardcoded, so an option never offers a
   *  subject nobody has authored or omits one added last week. */
  getQuestionSetFacets: (type?: 'test' | 'practice-set') =>
    fetchWithAdmin<AdminQuestionSetFacets>(
      `/console/question-sets/facets${type ? `?type=${type}` : ''}`),

  /** One student report, whole. The existing read route already admits a
   *  platform role via `canWriteReportsFor`, so this needs no admin-specific
   *  endpoint — only the studentId, which is the row's partition key. */
  getStudentReport: (reportId: string, studentId: string) =>
    fetchWithAdmin<{ report: AdminStudentReport }>(
      `/student-reports/${reportId}?studentId=${encodeURIComponent(studentId)}`),

  /** Operations → Test Report: every generation, produced or refused. */
  getTestReportRuns: (limit = 50) =>
    fetchWithAdmin<{ runs: TestReportRunRow[] }>(`/console/test-report-runs?limit=${limit}`),

  /** By Student: what one student was given and what they did with it. */
  getStudentPapers: (studentId: string) =>
    fetchWithAdmin<{ student: { id: string; name?: string }; papers: StudentPaperRow[] }>(
      `/console/students/${studentId}/papers`),

  /** Who has sat one paper, and whether each sitting produced a report. */
  getQuestionSetSittings: (questionSetId: string) =>
    fetchWithAdmin<{ paper: AdminPaperHead; sittings: AdminSittingRow[] }>(
      `/console/question-sets/${questionSetId}/sittings`),

  /** Generate a test report for a sitting that already happened. Forces past
   *  the write-back (long since fired) and the feature gate (a generated report
   *  is a draft, and drafts are staff-only). */
  generateTestReportForSitting: (sessionId: string) =>
    fetchWithAdmin<{ sessionId: string; userId: string; results: number; hasMarkingScheme: boolean; existingReportId?: string }>(
      `/console/test-sittings/${sessionId}/report`, { method: 'POST' }),

  /** Re-detect learning moments for every analysed question of one completed
   *  sitting. Enqueues a job — a sitting is one LLM call per question — and
   *  replaces the sitting's encounters rather than adding to them. A run
   *  already queued or running is handed back instead of queued again. Not
   *  subject to the auto-detect gate: a person asked. */
  detectLearningMomentsForSitting: (sessionId: string) =>
    fetchWithAdmin<SittingDetectionRequested>(
      `/console/test-sittings/${sessionId}/detect-learning-moments`, { method: 'POST' }),

  /** The same, for every completed sitting of a paper — one job per sitting.
   *  Sittings still open are listed under `skipped`, not queued. */
  detectLearningMomentsForPaper: (questionSetId: string) =>
    fetchWithAdmin<{
      questionSetId: string;
      queued: SittingDetectionRequested[];
      skipped: Array<{ sessionId: string; userId: string; reason: 'not-completed' }>;
    }>(`/console/question-sets/${questionSetId}/detect-learning-moments`, { method: 'POST' }),

  /** Question-session-report runs (durable OperationRun records — one per report
   *  generation for a submitted attempt). See operation-run-records.md. */
  getQuestionSessionReportRuns: (params?: { status?: string; sessionId?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    // Lets a caller holding only a session id find its run.
    if (params?.sessionId) query.set('sessionId', params.sessionId);
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return fetchWithAdmin<{ items: QuestionSessionReportRunView[] }>(
      `/console/question-session-report-runs${qs ? `?${qs}` : ''}`);
  },

  /** Async-job tuning: effective + handler-default config per registered type.
   *  Overrides land in the `async-job-config` settings doc and take effect
   *  within the resolver's 60s cache — no deploy. */
  getAsyncJobConfig: () =>
    fetchWithAdmin<{
      types: AsyncJobTypeConfig[];
      dispatcher: { schedule: string; source: 'app-setting' | 'default'; signalOnEnqueue: boolean };
    }>('/console/settings/async-jobs'),

  /** Pickup (queued→started) and execution (started→completed) percentiles per
   *  job type. `window` is one of 1h | 4h | 1d | 7d. */
  getAsyncJobTimings: (window: string) =>
    fetchWithAdmin<{ window: string; types: AsyncJobTypeStats[] }>(
      `/console/async-jobs/timings?window=${encodeURIComponent(window)}`),

  /** Save the WHOLE override map — a type omitted here reverts to its default. */
  saveAsyncJobConfig: (overrides: Record<string, Partial<AsyncJobConfigValues>>) =>
    fetchWithAdmin<{ saved: true; types: string[] }>('/console/settings/async-jobs', {
      method: 'PUT',
      body: JSON.stringify({ overrides }),
    }),

  /** One question-session-report run, by run id (cross-partition read). */
  getQuestionSessionReportRun: (id: string) =>
    fetchWithAdmin<{ run: QuestionSessionReportRunView }>(`/console/question-session-report-runs/${id}`),

  /** One-shot attempt-marking runs (durable OperationRun records — one per
   *  completed attempt marked against its scheme). See operation-run-records.md. */
  getMarkAttemptRuns: (params?: { status?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return fetchWithAdmin<{ items: MarkAttemptRunView[] }>(
      `/console/mark-attempt-runs${qs ? `?${qs}` : ''}`);
  },

  /** One attempt-marking run, by run id (cross-partition read). */
  getMarkAttemptRun: (id: string) =>
    fetchWithAdmin<{ run: MarkAttemptRunView }>(`/console/mark-attempt-runs/${id}`),

  getCohortClusterRuns: (params?: { status?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return fetchWithAdmin<{ items: CohortClusterRunView[] }>(
      `/console/cohort-cluster-runs${qs ? `?${qs}` : ''}`);
  },

  /** One clustering run, by run id (cross-partition read). */
  getCohortClusterRun: (id: string) =>
    fetchWithAdmin<{ run: CohortClusterRunView }>(`/console/cohort-cluster-runs/${id}`),

  getAsyncJobsMetrics: (windowMinutes = 60) =>
    fetchWithAdmin<{
      windowMinutes: number;
      failed: number;
      succeeded: number;
      queuedNow: number;
      runningNow: number;
      avgWaitSeconds: number | null;
    }>(`/console/async-jobs/metrics?windowMinutes=${windowMinutes}`),

  getAsyncJobDetail: (id: string) =>
    fetchWithAdmin<{ job: {
      id: string;
      userId: string;
      type: string;
      status: 'queued' | 'running' | 'completed' | 'failed' | 'dead';
      attempts: number;
      maxAttempts: number;
      queuedAt: string;
      startedAt?: string;
      completedAt?: string;
      nextAttemptAt?: string;
      claimedBy?: string;
      correlationObjectType?: string;
      correlationObjectId?: string;
      metadata?: Record<string, string>;
      dedupKey?: string;
      lastError?: { message: string; type?: string; attemptedAt: string };
      latencyMs?: number;
      payload: unknown;
      result: unknown;
    }; }>(`/console/async-jobs/${encodeURIComponent(id)}`),

  retryAsyncJob: (id: string) =>
    fetchWithAdmin<{ job: { id: string; status: string } }>(`/console/async-jobs/${encodeURIComponent(id)}/retry`, {
      method: 'POST',
    }),

  markAsyncJobDead: (id: string) =>
    fetchWithAdmin<{ job: { id: string; status: string } }>(`/console/async-jobs/${encodeURIComponent(id)}/mark-dead`, {
      method: 'POST',
    }),

  // Per-container size + count stats over the last 100 docs by `_ts DESC`.
  // Used by Monitoring → Container Stats. See
  // Backend/functions/src/admin/monitoring/containerStats.ts.
  getContainerStats: () =>
    fetchWithAdmin<{
      generatedAt: string;
      containers: Array<{
        name: string;
        docCount: number;
        sampled: number;
        sizeBytes: { min: number; max: number; median: number; avg: number; total: number };
        newest: { ts: string; id: string } | null;
        error?: string;
      }>;
    }>(`/console/container-stats`),

  // Teachers list (for bulk-tag teacher selector / teacher-stats filter).
  // Pass `tenantId` to scope to one tenant; omit (or pass undefined) for all.
  getTeachers: (tenantId?: string) => {
    const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    return fetchWithAdmin<{ userId: string; name: string }[]>(`/teachers${qs}`);
  },

  // Bulk-tag questions on behalf of a user (userId optional → current admin; system
  // tags like ForPlacement route to the shared pool server-side).
  bulkTagForUser: (userId: string | undefined, tag: string, questionIds: string[]) =>
    fetchWithAdmin<{ tag: string; tagged: number; skipped: number }>('/console/user-tags/bulk-tag', {
      method: 'POST',
      body: JSON.stringify({ ...(userId ? { userId } : {}), tag, questionIds }),
    }),
};

// Merged adminApi — backward compatible with all consumers

/** One gold document's result under one eval label. */
export interface ExtractionEvalDocument {
  documentId: string;
  documentName?: string;
  runs: number;
  meanScore: number;
  minScore: number;
  maxScore: number;
  meanRecall: number;
  provider?: string;
  costUsd: number;
  /** Wrong in EVERY run — what a fix can target. */
  systematic: string[];
  /** Wrong in some runs only — the noise a single run would misreport. */
  intermittent: string[];
}

export interface ExtractionEvalLabel {
  label: string;
  rubricVersion: string;
  createdAt: string;
  documents: ExtractionEvalDocument[];
  macroAverage: number;
  totalCostUsd: number;
}

/** Per-field outcome. `accepted_variant` = matched a documented ambiguity in the
 *  source, which is a match rather than a miss — the paper is what is unclear. */
export interface ExtractionFieldScore {
  verdict: 'exact' | 'lenient' | 'accepted_variant' | 'mismatch' | 'missing';
  score: number;
  /** Both populated on a mismatch — the page vs what the model wrote. */
  expected?: string;
  actual?: string;
}

export interface ExtractionQuestionScore {
  questionNumber: string;
  matchBasis: 'questionNumber' | 'similarity' | 'unmatched';
  stem: ExtractionFieldScore;
  mathTokens: ExtractionFieldScore;
  options?: ExtractionFieldScore;
  correctAnswer: ExtractionFieldScore;
  questionType: ExtractionFieldScore;
  weightedScore: number;
}

/** Every repeat of one document under one label. Repeats are kept apart because
 *  failing in ALL runs and failing in ONE are different findings. */
export interface ExtractionEvalDetail {
  label: string;
  documentId: string;
  documentName?: string;
  runs: Array<{
    runIndex: number;
    provider?: string;
    overallScore: number;
    recall: number;
    precision: number;
    droppedQuestionNumbers: string[];
    unmatchedPredictionNumbers: string[];
    swallowedNonQuestions: { predictionNumber: string; matched: string }[];
    perQuestion: ExtractionQuestionScore[];
  }>;
}


/** Filters the Assignments & Tests list can push to the server. Every one of
 *  them narrows in Cosmos rather than in the browser: the corpus is the whole
 *  system, so a client-side filter would be filtering one page of it. */
export interface AdminQuestionSetSearchArgs {
  type?: 'test' | 'practice-set';
  status?: 'draft' | 'published';
  visibility?: 'public' | 'private';
  subject?: string;
  exam?: string;
  class?: string;
  ownerId?: string;
  tenantId?: string;
  hasMarkingScheme?: boolean;
  createdFrom?: string;
  createdTo?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface AdminQuestionSetListRow {
  id: string;
  title: string;
  modality: 'practice' | 'test';
  status: string;
  visibility: string;
  subjects: string[];
  totalQuestions: number;
  hasMarkingScheme: boolean;
  createdAt: string;
  createdBy: string;
  ownerName?: string;
  tenantId?: string;
  tenantName?: string;
  /** Model spend: importing the paper plus every attempt at it. */
  llmCostUsd?: number;
  llmCost?: { importUsd: number; attemptsUsd: number };
  sittings: number;
  assignments: number;
  reports: number;
}


export interface AdminPaperHead {
  id: string;
  title: string;
  modality: string;
  hasMarkingScheme: boolean;
}

/** What a sitting-grain re-detect request came back with. */
export interface SittingDetectionRequested {
  sessionId: string;
  userId: string;
  jobId: string;
  /** The job was already queued or running; `jobId` is that one's. */
  alreadyRunning: boolean;
  questions: number;
  /** One call per chunk of the sitting, or one per question — the student's
   *  learning_moment_sitting_detect gate decides unless the request said. */
  combined: boolean;
}

export interface AdminSittingRow {
  sessionId: string;
  userId: string;
  studentName?: string;
  status: string;
  startedAt?: string;
  endedAt?: string;
  results: number;
  pending: number;
  reportId?: string;
  reportStatus?: string;
  /** Automatic checks the drafted report did not satisfy. Not an error — the
   *  report exists and reads fine; this is what to glance at before release. */
  reviewFlagCount?: number;
  /** The most recent generation attempt, when it produced no report. */
  lastJobStatus?: string;
  lastJobError?: string;
  /** Model spend on this attempt: page splits and every answer's analysis. */
  llmCostUsd?: number;
}


export interface AdminQuestionSetFacets {
  subjects: string[];
  exams: string[];
  classes: string[];
  owners: { id: string; name: string }[];
  tenants?: { id: string; name: string }[];
}

export type StudentPaperState = 'not-started' | 'in-progress' | 'completed' | 'declined';

export interface StudentPaperRow {
  questionSetId: string;
  title: string;
  modality: 'practice' | 'test';
  state: StudentPaperState;
  assignedAt?: string;
  assignedByName?: string;
  dueAt?: string;
  sittings: number;
  lastSessionId?: string;
  lastSatAt?: string;
  marks?: number;
  maxMarks?: number;
  reportId?: string;
  reportStatus?: string;
  reviewFlagCount?: number;
}


/** A generated test report, as the admin viewer reads it. `body` and `ledger`
 *  are typed loosely on purpose: their shapes live with the generator in the
 *  backend, and a second copy here would be free to drift from it. */
export interface AdminStudentReport {
  id: string;
  studentId: string;
  title: string;
  reportType: string;
  status: string;
  audience: string;
  createdAt: string;
  limits?: string;
  promptId?: string;
  testAttempt?: { questionSetId: string; sessionId: string };
  body?: Record<string, unknown>;
  ledger?: Record<string, unknown>;
  prediction?: Record<string, unknown>;
  predictionOutcome?: Record<string, unknown>;
  citations?: { label: string; ref: { kind: string; id: string } }[];
  /** What the automatic checks could not vouch for. Present means "read this
   *  before releasing", not "this is wrong". */
  reviewFlags?: { kind: 'figure' | 'shape'; field?: string; value?: number; detail: string }[];
}


export interface TestReportRunRow {
  reportId?: string;
  reportStatus?: string;
  reviewFlagCount?: number;
  sessionId: string;
  studentId: string;
  studentName?: string;
  questionSetId?: string;
  paperTitle?: string;
  status: string;
  error?: string;
  at: string;
  promptId?: string;
  hasMarkingScheme?: boolean;
}

export const adminApi = {
  ...questionApi,
  ...modelApi,
  ...userApi,
  ...curriculumApi,
  ...booksApi,
  ...schoolsApi,
  ...conceptsApi,
  ...platformExamApi,
  ...coreApi,
};
